import React, { useState, useEffect } from "react";
import {
  Button,
  TextInput,
  Textarea,
  Stack,
  Group,
  LoadingOverlay,
  Box,
  Text,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import Repository from "../../../_base/helper/HttpHelper";
import { modifyUserCommentReply } from "../../../api/ApiUserComment";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { title } from "process";
import { IconCheck, IconX } from "@tabler/icons-react";

type Props = {
  id: number;
  onClose: (shouldRefresh?: any) => void;
  isEditMode: boolean;
};

interface ReplyCommentData {
  id: number;
  commentId: number;
  isUserAdmin: number;
  userId: number;
  userEmail: string;
  userName: string;
  userAvatar: string;
  userNote: string;
  rate: number;
  title: string;
  content: string;
  files: string;
  approved: number;
  postTime: string;
  orderNumber: number;
  isFeatured: number;
  peopleIdVote: string;
  peopleLikeCount: number;
  peopleDislikeCount: number;
  creationDate: string;
  createdBy: string;
  lastUpdateDate: string;
  lastUpdateBy: string;
}

const EditReplyComments = ({ id, onClose, isEditMode }: Props) => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  const form = useForm<ReplyCommentData>({
    initialValues: {
      id: 0,
      commentId: 0,
      isUserAdmin: 0,
      userId: 0,
      userEmail: "userEmail@gmail.com",
      userName: "userName",
      userAvatar: "",
      userNote: "",
      rate: 0,
      title: "",
      content: "",
      files: "",
      approved: 0,
      postTime: "",
      orderNumber: 0,
      isFeatured: 0,
      peopleIdVote: "",
      peopleLikeCount: 0,
      peopleDislikeCount: 0,
      creationDate: "",
      createdBy: "",
      lastUpdateDate: "",
      lastUpdateBy: "",
    },
    validate: {
      content: (value) => {
        if (!value || value.trim().length === 0) {
          return "Nội dung không được để trống";
        }
        if (value.trim().length < 5) {
          return "Nội dung phải có ít nhất 5 ký tự";
        }
        // if (value.trim().length > 2000) {
        //   return "Nội dung không được vượt quá 2000 ký tự";
        // }
        // Kiểm tra nội dung có chứa ký tự đặc biệt không phù hợp
        const specialCharsRegex = /[<>\"'&]/;
        if (specialCharsRegex.test(value)) {
          return "Nội dung không được chứa các ký tự đặc biệt như <, >, \", ', &";
        }
        return null;
      },
    },
    transformValues: (values) => ({
      ...values,
      content: values.content?.trim(),
    }),
  });

  const getDetail = async (id: number) => {
    try {
      setInitialLoading(true);
      const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);
      const response = await repository.get(
        `/TblUserCommentReply/get-detail/${id}`
      );

      if (response && response.success) {
        const data = response.data;

        const actualData = Array.isArray(data) ? data[0] : data;

        const formValues = {
          id: actualData?.id || 0,
          commentId: actualData?.commentId,
          isUserAdmin: actualData?.isUserAdmin || null,
          userId: actualData?.userId || null,
          userEmail: actualData?.userEmail || "",
          userName: actualData?.userName || null,
          userAvatar: actualData?.userAvatar || null,
          userNote: actualData?.userNote || null,
          rate: actualData?.rate || null,
          title: actualData?.title || null,
          content: actualData?.content || "",
          files: actualData?.files || null,
          approved: actualData?.approved,
          postTime: actualData?.postTime || null,
          orderNumber: actualData?.orderNumber || null,
          isFeatured: actualData?.isFeatured || null,
          peopleIdVote: actualData?.peopleIdVote || null,
          peopleLikeCount: actualData?.peopleLikeCount || null,
          peopleDislikeCount: actualData?.peopleDislikeCount || null,
          creationDate: actualData?.creationDate || null,
          createdBy: actualData?.createdBy || null,
          lastUpdateDate: actualData?.lastUpdateDate || null,
          lastUpdateBy: actualData?.lastUpdateBy || null,
        };

        console.log("Setting form values:", formValues);
        form.setValues(formValues);

        // Verify form values after setting
        setTimeout(() => {
          console.log("Form values after set:", form.values);
          setDataLoaded(true);
        }, 100);
      } else {
        NotificationExtension.Fails("Không thể tải dữ liệu chi tiết!");
      }
    } catch (error) {
      console.error("Error fetching detail:", error);
      NotificationExtension.Fails("Có lỗi xảy ra khi tải dữ liệu!");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values: ReplyCommentData) => {
    console.log("handleSubmit called with values:", values);
    try {
      setLoading(true);
      console.log("Starting update...");

      const updateData = {
        id: values.id,
        commentId: values.commentId,
        isUserAdmin: values.isUserAdmin,
        userId: values.userId,
        userEmail: values.userEmail,
        userName: values.userName,
        userAvatar: values.userAvatar,
        userNote: values.userNote,
        rate: values.rate,
        title: values.title,
        content: values.content?.trim(),
        files: values.files,
        approved: 0,
        postTime: values.postTime,
        orderNumber: values.orderNumber,
        isFeatured: values.isFeatured,
        peopleIdVote: values.peopleIdVote,
        peopleLikeCount: values.peopleLikeCount,
        peopleDislikeCount: values.peopleDislikeCount,
        creationDate: values.creationDate,
        createdBy: values.createdBy,
        lastUpdateDate: values.lastUpdateDate,
        lastUpdateBy: values.lastUpdateBy,
      };

      const response = await modifyUserCommentReply(updateData);

      if (response && response.success) {
        modals.closeAll();
        onClose(true);
      }
    } catch (error) {
      console.error("Error updating reply comment:", error);
      NotificationExtension.Fails("Có lỗi xảy ra khi cập nhật!");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    modals.closeAll();
  };

  useEffect(() => {
    if (id && isEditMode) {
      setDataLoaded(false);
      getDetail(id);
    } else {
      setInitialLoading(false);
    }
  }, [id, isEditMode]);

  if (initialLoading) {
    return (
      <Box style={{ position: "relative", minHeight: 200 }}>
        <LoadingOverlay visible={true} />
      </Box>
    );
  }

  return (
    <Box
      key={`edit-reply-${id}-${dataLoaded}`}
      mx="auto"
      style={{ position: "relative", width: "500px" }}
    >
      <LoadingOverlay visible={loading} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* {(form.values.userName || form.values.userEmail) && (
            <>
              <Divider label="Thông tin khách hàng" labelPosition="left" />
              <Group grow>
                {form.values.userName && (
                  <TextInput
                    label="Tên khách hàng"
                    value={form.values.userName}
                    disabled
                    style={{ opacity: 0.7 }}
                  />
                )}

                {form.values.userEmail && (
                  <TextInput
                    label="Email khách hàng"
                    value={form.values.userEmail}
                    disabled
                    style={{ opacity: 0.7 }}
                  />
                )}
              </Group>
              <Divider />
            </>
          )} */}

          <Textarea
            label="Nội dung phản hồi"
            // description={`${form.values.content?.length}/2000 ký tự`}
            placeholder="Nhập nội dung phản hồi..."
            required
            minRows={4}
            maxRows={10}
            autosize
            {...form.getInputProps("content")}
          />

          <Group justify="flex-end" mt="md">
            <Button
              leftSection={<IconX size={16} />}
              color="red"
              onClick={handleCancel}
              disabled={loading}
            >
              Hủy
            </Button>
            <Button
              leftSection={<IconCheck size={16} />}
              type="submit"
              loading={loading}
              disabled={!form.isValid() || loading}
              color="blue"
            >
              Cập nhật
            </Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
};
export default EditReplyComments;
